<template>
  <section class="flex flex-col gap-5">
    <form
      @submit.prevent="submitInfo()"
      class="grid grid-cols-12 gap-x-5 text-black"
      autocomplete="off"
    >
      <div class="flex justify-between items-center col-span-12">
        <h4>{{ $t("objectives.titles.info") }}</h4>
        <Button
          :label="$t('labels.save')"
          type="submit"
          class="btn-primary p-button-sm"
          :loading="loading"
          v-if="role === 'TEACHER'"
        />
      </div>

      <div
        class="flex flex-col my-4 col-span-12"
        v-if="
          infoData?.id == undefined && predecessor && objectiveClicked == null
        "
      >
        <p class="mb-2">{{ $t("objectives.inputs.predecessor") }}</p>
        <AutoComplete
          id="categories-input"
          optionLabel="name"
          v-model="autocomplete.selected"
          :suggestions="autocomplete.sugestions"
          :forceSelection="true"
          :delay="500"
          @complete="searchForObjectives($event)"
        />
      </div>
      <div class="my-4 col-span-12">
        <p class="mb-2">{{ $t("objectives.inputs.name") }}</p>
        <InputText
          id="title-input"
          type="text"
          name="name"
          v-model="infoData.name"
          :class="v$.infoData.name.$error ? 'p-invalid' : ''"
          :maxlength="50"
          :disabled="role === 'ADMINISTRATOR'"
        />
        <Errors
          :errors="v$.infoData.name.$errors"
          :actualLength="infoData.name?.length"
          :maxLength="50"
        />
      </div>
      <div class="my-4 col-span-12">
        <p class="mb-2">{{ $t("objectives.inputs.description") }}</p>
        <Textarea
          id="description-input"
          :autoResize="true"
          rows="10"
          cols="30"
          v-model="infoData.description"
          :class="v$.infoData.description.$error ? 'p-invalid' : ''"
          :maxlength="999"
          :disabled="role === 'ADMINISTRATOR'"
        />
        <Errors
          :errors="v$.infoData.description.$errors"
          :actualLength="infoData.description?.length"
          :maxLength="999"
        />
      </div>
    </form>
  </section>
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { InfoObjective, Objective } from "@/models/objective.model";
import { ObjectivesRest } from "@/services/rest/objectives.rest";
import { GoToRoute } from "@/utils/go-to.util";
import {
  getItemLocalStorage,
  removeItemLocalStorage
} from "@/utils/save-storage.util";
import useVuelidate from "@vuelidate/core";
import { delay } from "rxjs";
import { defineComponent } from "vue";
import { ObjectiveService } from "../objective.service";
import { InfoObjectiveService } from "./info.service";

export default defineComponent({
  name: "formInfo",
  emits: ["onNext", "onAllow"],
  computed: {
    rest(): ObjectivesRest {
      return new ObjectivesRest();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  props: {
    role: [String],
  },
  data() {
    return {
      loading: false as boolean,
      autocomplete: {
        sugestions: [] as any,
        selected: null as any,
      },
      predecessor: false as boolean,
      params: {
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        id: this.$route.params.id ?? "",
        tab: 1,
      },
      infoData: {} as InfoObjective,
      objectiveClicked: "",
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      infoData: {
        name: { required },
        description: { required },
      },
    };
  },
  mounted() {
    this.getInfo();
  },
  created() {
    this.objectiveClicked = JSON.parse(getItemLocalStorage("objectiveId"));
    this.hasPredecessor();
  },
  watch: {
    $route() {
      removeItemLocalStorage("length");
    },
  },
  methods: {
    getInfo(): void {
      const objectiveId: any = this.$route.params.id;
      if (objectiveId) {
        InfoObjectiveService.info.subscribe({
          next: (response) => {
            this.infoData = response;
            removeItemLocalStorage("length");
          },
        });
      }
    },
    submitInfo(): void {
      this.v$.infoData.$touch();
      if (!this.v$.infoData.$error) {
        this.loading = true;
        const data = new Objective();
        data.id = this.infoData?.id ?? undefined;
        data.name = this.infoData?.name ?? "";
        data.description = this.infoData?.description ?? "";
        data.predecessorId =
          this.autocomplete.selected?.id ?? this.objectiveClicked;
        data.required = true;

        if (data?.id) {
          this.update(data);
        } else {
          this.create(data);
        }
      }
    },
    create(data: Objective): void {
      const chapterId: any = this.$route.params.chapterId;
      this.rest
        .postObjective(chapterId, data)
        .pipe(delay(1500))
        .subscribe({
          next: (response) => {
            InfoObjectiveService.setInfoObjective(response);
            this.nextStep(response?.id);
          },
          error: () => {
            this.loading = false;
          },
        });
    },
    update(data: Objective): void {
      const chapterId: any = this.$route.params.chapterId;
      const trailId: any = this.$route.params.id ?? this.infoData?.id;
      this.rest
        .updateObjective(chapterId, trailId, data)
        .pipe(delay(1500))
        .subscribe({
          next: (response) => {
            InfoObjectiveService.setInfoObjective(response);
            this.nextStep(response.id);
          },
          error: () => {
            this.loading = false;
          },
        });
    },
    searchForObjectives(event: any): void {
      const chapterId: any = this.$route.params.chapterId;
      const { query } = event;
      this.service.objectives.subscribe({
        next: (response) => {
          this.autocomplete.sugestions = response.filter(
            (e: any) => e.id !== this.infoData.id
          );
        },
      });
      this.service.getObjectives(chapterId, {
        page: 0,
        size: 100,
        name: query.toLowerCase(),
      });
    },
    hasPredecessor(): void {
      const e = JSON.parse(getItemLocalStorage("length"));
      if (e?.learningObjectives?.length >= 1) {
        this.predecessor = true;
      }
    },
    nextStep(id?: any): void {
      this.params.id = id;
      this.$emit("onNext", 1);
      this.$emit("onAllow", 0);
      this.go.redirectToPage("formObjective", this.params);
      this.loading = false;
    },
  },
});
</script>
